import type { AssetComponentModule, PrefetchAssetsModule } from "./types";
import { REMOTE_NAME, getRemoteEntryUrl } from "./config";

const SCRIPT_LOAD_TIMEOUT_MS = 15_000;

export interface LoadResult {
  assetModule: AssetComponentModule;
  prefetchModule: PrefetchAssetsModule;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  remoteReact: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  remoteReactDOM: any;
}

interface RemoteContainer {
  init: (shareScope: Record<string, unknown>) => Promise<void>;
  get: (module: string) => Promise<() => unknown>;
}

const scriptElements = new Map<string, HTMLScriptElement>();

let loadPromise: Promise<LoadResult> | null = null;

function loadScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(
        new Error(
          `Script load timed out after ${SCRIPT_LOAD_TIMEOUT_MS}ms: ${url}`,
        ),
      );
    }, SCRIPT_LOAD_TIMEOUT_MS);

    const done = () => clearTimeout(timeout);

    const existing = scriptElements.get(url);

    if (existing) {
      if (existing.dataset.loaded === "true") {
        done();
        resolve();
        return;
      }
      if (existing.dataset.loaded === "error") {
        done();
        reject(new Error(`Previously failed to load: ${url}`));
        return;
      }
      existing.addEventListener(
        "load",
        () => {
          done();
          resolve();
        },
        { once: true },
      );
      existing.addEventListener(
        "error",
        () => {
          done();
          reject(new Error(`Failed to load: ${url}`));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = url;
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => {
      done();
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => {
      done();
      script.dataset.loaded = "error";
      reject(new Error(`Failed to load: ${url}`));
    };

    scriptElements.set(url, script);
    document.head.appendChild(script);
  });
}

/**
 * Find the first available React 18 entry in the share scope.
 * The remote registers its bundled React — we pick the first 18.x version.
 * Warns if no 18.x match is found and falls back to the first available version.
 */
function findReact18Entry(
  label: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scopeEntry: Record<string, any> | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any | undefined {
  if (!scopeEntry) return undefined;

  for (const version of Object.keys(scopeEntry)) {
    if (version.startsWith("18.")) {
      return scopeEntry[version];
    }
  }

  const versions = Object.keys(scopeEntry);
  if (versions.length > 0) {
    console.warn(
      `[AssetRenderer] No React 18.x found for "${label}". Available: [${versions.join(", ")}]. Falling back to ${versions[0]}.`,
    );
    return scopeEntry[versions[0]!];
  }

  return undefined;
}

export function loadAssetRenderingService(env?: string): Promise<LoadResult> {
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const url = getRemoteEntryUrl(env);
    await loadScript(url);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const container = (window as any)[REMOTE_NAME] as
      | RemoteContainer
      | undefined;
    if (!container) throw new Error(`Container "${REMOTE_NAME}" not found`);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const shareScope: Record<string, Record<string, any>> = {};
    await container.init(shareScope);

    const assetFactory = await container.get("./Asset");
    const prefetchFactory = await container.get("./prefetchAssets");

    const reactEntry = findReact18Entry("react", shareScope.react);
    const reactDomEntry = findReact18Entry(
      "react-dom",
      shareScope["react-dom"],
    );

    let remoteReact: unknown;
    let remoteReactDOM: unknown;

    if (reactEntry?.get) {
      const factory = reactEntry.get();
      remoteReact = typeof factory === "function" ? factory() : await factory;
    }
    if (reactDomEntry?.get) {
      const factory = reactDomEntry.get();
      remoteReactDOM =
        typeof factory === "function" ? factory() : await factory;
    }

    return {
      assetModule: assetFactory() as AssetComponentModule,
      prefetchModule: prefetchFactory() as PrefetchAssetsModule,
      remoteReact,
      remoteReactDOM,
    };
  })();

  loadPromise.catch(() => {
    loadPromise = null;
  });
  return loadPromise;
}

export function resetLoader(): void {
  loadPromise = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  delete w[REMOTE_NAME];

  for (const [url, script] of scriptElements) {
    script.remove();
    scriptElements.delete(url);
  }
}
